import React, { useEffect } from 'react';
import { ChartCard, HorizontalBars, InfoTooltip } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';
import {
  type RelativePerformance,
  getRelativePerformance,
} from '../-helpers/getRelativePerformance';
import { useSortedChartData } from '../-hooks/useSortedChartData';
import { OptionalKey } from '../route';

export const KPIImpact_IncreasingMedicalAccessFragment = gql(/* GraphQL */ `
  fragment KPIImpact_IncreasingMedicalAccessFragment on HealthcareIndicators {
    patientsReceivingNewAccess {
      trimmedAvg
      isDataSuppressed
    }
    patientsReceivingNewAccess_n {
      trimmedAvg
      isDataSuppressed
    }
    patientsReceivingNewAccess_pace {
      trimmedAvg
      isDataSuppressed
    }
  }
`);

type IncreasingMedicalAccessProps = {
  className?: string;
  indicators: FragmentType<typeof KPIImpact_IncreasingMedicalAccessFragment>;
  viewerIndicators?: FragmentType<typeof KPIImpact_IncreasingMedicalAccessFragment>;
  investmentLevel: OptionalKey;
  onEvaluatePerformance: (key: string, performance: RelativePerformance) => void;
};

export function IncreasingMedicalAccess({
  indicators,
  viewerIndicators,
  investmentLevel,
  className,
  onEvaluatePerformance,
}: IncreasingMedicalAccessProps) {
  const benchmarkData = useFragment(KPIImpact_IncreasingMedicalAccessFragment, indicators);
  const viewerData = useFragment(KPIImpact_IncreasingMedicalAccessFragment, viewerIndicators);

  const benchmarkDataMap = {
    company: benchmarkData.patientsReceivingNewAccess,
    investment: benchmarkData.patientsReceivingNewAccess_n,
    rate_of_change: benchmarkData.patientsReceivingNewAccess_pace,
  };

  const viewerDataMap = {
    company: viewerData?.patientsReceivingNewAccess,
    investment: viewerData?.patientsReceivingNewAccess_n,
    rate_of_change: viewerData?.patientsReceivingNewAccess_pace,
  };

  const selectedBenchmarkData = benchmarkDataMap[investmentLevel];
  const selectedViewerData = viewerDataMap[investmentLevel];

  const { t, title, titleTooltipKPI } = useChartTranslations('increasingMedicalAccess');

  const getDisplayValue = (value?: number | null): string => {
    if (value == null) return '';
    const strValue = value.toLocaleString('en', { maximumFractionDigits: 0 });
    return t('displayValue', { value: strValue }) ?? strValue;
  };

  const { sortedChartData } = useSortedChartData({
    selectedBenchmarkData,
    selectedViewerData,
    getDisplayValue,
  });

  const relativePerformance = getRelativePerformance(
    selectedBenchmarkData?.trimmedAvg,
    selectedViewerData?.trimmedAvg
  );

  useEffect(() => {
    onEvaluatePerformance('increasingMedicalAccess', relativePerformance);
  }, [onEvaluatePerformance, relativePerformance]);

  return (
    <ChartCard className={className}>
      <ChartCard.Header className="flex items-center justify-between">
        <ChartCard.Title>
          {title}
          {titleTooltipKPI && <InfoTooltip>{titleTooltipKPI}</InfoTooltip>}
        </ChartCard.Title>
        {/* <Button variant="outline" className="shrink-0 font-semibold uppercase">
          view details
        </Button> */}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={selectedBenchmarkData.isDataSuppressed}>
        <HorizontalBars
          min={Math.min(
            0,
            selectedViewerData?.trimmedAvg ?? 0,
            selectedBenchmarkData.trimmedAvg ?? 0
          )}
          max={Math.max(
            100,
            selectedViewerData?.trimmedAvg ?? 0,
            selectedBenchmarkData.trimmedAvg ?? 0
          )}
          showInlineDisplayValue={true}
        >
          {sortedChartData.map((data, index) => (
            <HorizontalBars.Item
              key={index}
              label={data.label}
              value={data.value}
              displayValue={data.displayValue}
              color={data.color}
              isDashed={data.isDashed}
            />
          ))}
        </HorizontalBars>

        {/* <Link href="/impact-kpis/land-managed" className="mt-5 lg:hidden">
          <Button variant="outline" size="small" className="shrink-0 font-semibold uppercase">
            view details
          </Button>
        </Link> */}
      </ChartCard.Body>
    </ChartCard>
  );
}
